import {
    KEY_NOTIFICATIONS_NOTIFY_MAX_HR,
    KEY_NOTIFICATIONS_NOTIFY_MAX_MECHANICAL_POWER,
    KEY_NOTIFICATIONS_NOTIFY_MAX_VEL,
    KEY_NOTIFICATIONS_NOTIFY_TEAMS,
    KEY_NOTIFICATIONS_SHOW_UNREAD_ONLY,
    useGetSetting,
    useGetTeams,
    useHasModule,
    useSettingMutation,
} from 'api';
import { useTranslations } from 'i18n';
import mixpanel from 'mixpanel-browser';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { usePrevious } from 'shared';
import { Button, Drawer, IconLoader, Select, Switch, Text } from 'ui';
import styles from './notifications-drawer.module.css';

interface SettingsDrawerProps {
    open: boolean;
    onBack: () => void;
    onClose: () => void;
}

export const SettingsDrawer: React.FC<SettingsDrawerProps> = (props) => {
    const { onBack, onClose, open } = props;
    const { __ } = useTranslations();
    const unreadOnlySetting = useGetSetting(KEY_NOTIFICATIONS_SHOW_UNREAD_ONLY);
    const maxVelSetting = useGetSetting(KEY_NOTIFICATIONS_NOTIFY_MAX_VEL);
    const maxHrSetting = useGetSetting(KEY_NOTIFICATIONS_NOTIFY_MAX_HR);
    const maxMechanicalPowerSetting = useGetSetting(KEY_NOTIFICATIONS_NOTIFY_MAX_MECHANICAL_POWER);
    const selectedTeamsSetting = useGetSetting(KEY_NOTIFICATIONS_NOTIFY_TEAMS);
    const hasMechanicalPower = useHasModule('MultidirectionalLoad.Released') === true;
    const settingMutation = useSettingMutation();
    const { data: teams = [] } = useGetTeams();

    const unreadOnly = unreadOnlySetting?.value === '1';
    const profileToggles = [
        {
            key: KEY_NOTIFICATIONS_NOTIFY_MAX_VEL,
            id: 'max-vel',
            label: 'notifications.when_new_max_vel',
            setting: maxVelSetting,
        },
        {
            key: KEY_NOTIFICATIONS_NOTIFY_MAX_HR,
            id: 'max-hr',
            label: 'notifications.when_new_max_hr',
            setting: maxHrSetting,
        },
        ...(hasMechanicalPower
            ? [
                  {
                      key: KEY_NOTIFICATIONS_NOTIFY_MAX_MECHANICAL_POWER,
                      id: 'max-mechanical-power',
                      label: 'notifications.when_new_max_mechanical_power',
                      setting: maxMechanicalPowerSetting,
                  },
              ]
            : []),
    ].map((toggle) => ({ ...toggle, checked: !toggle.setting || toggle.setting.value === '1' }));
    const selectedTeamIds = selectedTeamsSetting
        ? selectedTeamsSetting.value.split(',')
        : teams.filter((team) => team.is_default).map((team) => team.id);
    const selectedTeams = teams.filter((team) => selectedTeamIds.includes(team.id));

    function toggleSetting(key: string, checked: boolean) {
        settingMutation.mutate({ key, value: checked ? '1' : '0' });
        mixpanel.track(`BellNotificationSettingsToggle${key}`, { toggleValue: checked });
    }

    function handleTeamsChange(newTeamIds: string[]) {
        const newTeams = teams.filter((team) => newTeamIds.includes(team.id));
        settingMutation.mutate({
            key: KEY_NOTIFICATIONS_NOTIFY_TEAMS,
            value: newTeams.map((team) => team.id).join(','),
        });
        mixpanel.track('BellNotificationSettingsUpdateTeams', { teams: newTeams.map((team) => team.name) });
    }

    const wasOpen = usePrevious(open);
    React.useEffect(() => {
        if (open !== wasOpen && wasOpen !== undefined) {
            mixpanel.track(open ? 'BellNotificationSettingsPanelOpened' : 'BellNotificationSettingsPanelClosed', {
                teams: selectedTeams.map((team) => team.name),
                [KEY_NOTIFICATIONS_SHOW_UNREAD_ONLY]: unreadOnly,
                ...Object.fromEntries(profileToggles.map((toggle) => [toggle.key, toggle.checked])),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, wasOpen]);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={__('misc.settings')}
            testId="notification-settings-drawer"
            className={styles['drawer']}
            bodyClassName={styles['drawer-body']}
        >
            <div className={styles['setting']}>
                <Button
                    size="sm"
                    color="ghost"
                    label={__('misc.back')}
                    startIcon={<IconLoader iconName="IconChevronLeft" size="sm" aria-hidden />}
                    onClick={onBack}
                    id="notification-settings-drawer-back-button"
                />
            </div>
            <Text as="h3" variant="title-medium" className={styles['section']}>
                {__('misc.general')}
            </Text>
            <div className={styles['setting']}>
                <Switch
                    id="notification-settings-toggle-unread-only"
                    checked={unreadOnly}
                    onChange={(checked) => toggleSetting(KEY_NOTIFICATIONS_SHOW_UNREAD_ONLY, checked)}
                    label={<Text as="span">{__('notifications.only_show_unread')}</Text>}
                />
            </div>
            <Text as="h3" variant="title-medium" className={styles['section']}>
                {__('notifications.activity_notifications')}
            </Text>
            <Text as="p" className={styles['setting']}>
                {__('notifications.i_want_to_be_notified')}
            </Text>
            {profileToggles.map(({ key, id, label, checked }) => (
                <div key={key} className={styles['setting']}>
                    <Switch
                        id={`notification-settings-toggle-${id}`}
                        checked={checked}
                        onChange={(value) => toggleSetting(key, value)}
                        label={
                            <Text as="span">
                                <ReactMarkdown disallowedElements={['p']} unwrapDisallowed>
                                    {__(label)}
                                </ReactMarkdown>
                            </Text>
                        }
                    />
                </div>
            ))}
            <div className={styles['setting']}>
                <Select
                    id="notification-settings-select-teams"
                    size="xs"
                    label={__('notifications.which_teams')}
                    value={__('misc.select')}
                    multiSelect
                    middleMenuItems={teams.map((team) => ({
                        id: team.id,
                        value: team.name,
                        isSelected: selectedTeamIds.includes(team.id),
                    }))}
                    onItemSelect={(item) =>
                        handleTeamsChange(
                            item.isSelected
                                ? [...selectedTeamIds, item.id]
                                : selectedTeamIds.filter((teamId) => teamId !== item.id),
                        )
                    }
                    onSelectAll={(updated) =>
                        handleTeamsChange(updated.filter((item) => item.isSelected).map((item) => item.id))
                    }
                />
            </div>
        </Drawer>
    );
};
